function _objectWithoutProperties(source, excluded) { if (source == null) return {}; var target = _objectWithoutPropertiesLoose(source, excluded); var key, i; if (Object.getOwnPropertySymbols) { var sourceSymbolKeys = Object.getOwnPropertySymbols(source); for (i = 0; i < sourceSymbolKeys.length; i++) { key = sourceSymbolKeys[i]; if (excluded.indexOf(key) >= 0) continue; if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue; target[key] = source[key]; } } return target; }

function _objectWithoutPropertiesLoose(source, excluded) { if (source == null) return {}; var target = {}; var sourceKeys = Object.keys(source); var key, i; for (i = 0; i < sourceKeys.length; i++) { key = sourceKeys[i]; if (excluded.indexOf(key) >= 0) continue; target[key] = source[key]; } return target; }

import _regeneratorRuntime from "@babel/runtime/regenerator";

/** @module store */
import { observer, inject, Provider } from "mobx-react";
import AppTree from "../components/AppTree";
import { rmlOpts, i18n, request as utilsRequest, fetchTree as utilFetchTree, applyNodeDefaults, humanFileSize, secondsFormat } from "../util";
import { updateTreeItemById } from "react-aiot";
import { types, flow, resolvePath, onPatch, resolveIdentifier, getSnapshot, getParent, clone } from "mobx-state-tree";
import TreeNode from "./TreeNode";
import Upload from "./Upload";
import $ from "jquery";
import React from "react";
/**
 * The main Mobx State Tree store for the RML application. It holds a static tree and
 * the fetched tree from the server.
 *
 * @class BasicStore
 * @property {int} [rootId=rmlOpts.others.rootId] The root folder id
 * @property {module:store/TreeNode~TreeNode[]} staticTree The static tree
 * @property {module:store/TreeNode~TreeNode[]} [tree] The tree
 * @property {string|int} [selectedId=0] The selected id
 */

var BasicStore = types.model("RMLBasicStore", {
  rootId: +rmlOpts.others.rootId,
  staticTree: types.array(TreeNode),
  tree: types.optional(types.array(TreeNode), []),
  slugs: types.optional(types.frozen(), {
    names: [],
    slugs: [],
    types: []
  }),
  selectedId: types.optional(types.union(types.string, types.number), 0) // Do not fill manually, it is filled in afterCreated through onPatch

}).views(function (self) {
  return {
    /**
     * Get tree item by id.
     *
     * @param {string|int} id
     * @param {boolean} [exlucdeStatic=true]
     * @returns {module:store/TreeNode~TreeNode} Tree node
     * @memberof module:store~BasicStore
     * @instance
     */
    getTreeItemById: function getTreeItemById(id) {
      var excludeStatic = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : true;

      if (id === false) {
        return undefined;
      }

      var result = resolveIdentifier(TreeNode, self, id);

      if (excludeStatic && self.staticTree.indexOf(result) > -1) {
        return undefined;
      }

      return result;
    },

    get selected() {
      return self.getTreeItemById(self.selectedId, false);
    },

    get breadcrumb() {
      var selected = this.selected;

      if (selected) {
        return selected.path.map(function (node) {
          return node.title;
        });
      } else {
        return [/*#__PURE__*/React.createElement("i", {
          key: "0"
        }, i18n("noneSelected"))];
      }
    }

  };
}).actions(function (self) {
  var currentlySettingTree = 0; // see onPatch in afterCreate()

  return {
    /**
     * The model is created so watch for specific properties. For example set
     * the selected property.
     *
     * @memberof module:store~BasicStore
     * @private
     * @instance
     */
    afterCreate: function afterCreate() {
      onPatch(self, function (_ref) {
        var op = _ref.op,
            path = _ref.path,
            value = _ref.value;

        // A new selected item is setted
        if ((path.startsWith("/tree/") || path.startsWith("/staticTree/")) && path.endsWith("/selected")) {
          var currentSelected = self.selected,
              obj = resolvePath(self, path.slice(0, path.length - 9));

          if (value === true) {
            currentSelected && currentSelected.id !== obj.id && currentSelected.setter(function (node) {
              node.selected = false;
            });

            self._setSelectedIdFromPath(obj);
          } else if (currentSelected === obj) {
            // Reset selected id
            self._setSelectedIdFromPath({
              id: undefined
            });
          }
        } else if (currentlySettingTree === 0 && op === "add" && /(tree|childNodes)\/\d+$/.test(path)) {
          // Listen to children changes when added to the tree so automatical orders are applied
          var applyTo = getParent(resolvePath(self, path), 2);

          if (typeof applyTo.applyChildrenOrder === "function" && applyTo.subOrderAutomatically) {
            applyTo.applyChildrenOrder();
          }
        }
      });
    },

    /**
     * Iterate a callback over all nodes within the static and/or normal tree.
     */
    nodes: function nodes(fn) {
      var isStatic = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : false;

      var fnRec = function fnRec() {
        var tree = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : isStatic ? self.staticTree : self.tree;
        return tree.forEach(function (n) {
          fn(n);
          n.childNodes && fnRec(n.childNodes);
        });
      };

      fnRec();
    },
    _setSelectedIdFromPath: function _setSelectedIdFromPath(obj) {
      self.selectedId = obj.id;
    },

    /**
     * Update this node attributes.
     *
     * @param {function} callback The callback with one argument (node draft)
     * @memberof module:store~BasicStore
     * @instance
     */
    setter: function setter(callback) {
      callback(self);
    },

    /**
     * Get a snapshot of tree without selection.
     *
     * @returns {object[]}
     */
    getTreeSnapshot: function getTreeSnapshot() {
      var snapshot = $.extend(true, [], getSnapshot(self.tree));
      updateTreeItemById(self.selectedId, snapshot, function (n) {
        return n.selected = false;
      });
      return snapshot;
    },

    /**
     * Set the tree.
     *
     * @param {object} tree The object representing a tree
     * @param {boolean} [isStatic=false]
     * @param {object} [slugs]
     * @memberof module:store~BasicStore
     * @instance
     */
    setTree: function setTree(tree) {
      var isStatic = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : false;
      var slugs = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : null;
      currentlySettingTree++;

      if (isStatic) {
        self.staticTree.clear();
        self.staticTree.replace(tree);
      } else {
        self.tree.clear();
        self.tree.replace(tree);
      }

      if (slugs) {
        self.slugs = slugs;
      }

      currentlySettingTree--;
    },

    /**
     * Handle sort mechanism.
     *
     * @returns {boolean}
     * @throws Error
     * @memberof module:store~Store
     * @instance
     */
    handleSort: flow( /*#__PURE__*/_regeneratorRuntime.mark(function _callee(_ref2) {
      var id, oldIndex, newIndex, parentFromId, parentToId, nextId, _ref2$request, request, tree, rootId, requestBody, treeItem;

      return _regeneratorRuntime.wrap(function _callee$(_context) {
        while (1) {
          switch (_context.prev = _context.next) {
            case 0:
              id = _ref2.id, oldIndex = _ref2.oldIndex, newIndex = _ref2.newIndex, parentFromId = _ref2.parentFromId, parentToId = _ref2.parentToId, nextId = _ref2.nextId, _ref2$request = _ref2.request, request = _ref2$request === void 0 ? true : _ref2$request;
              tree = self.tree, rootId = self.rootId;
              requestBody = {
                nextId: nextId === 0 ? false : nextId
              }; // Find parent trees with children

              if (parentFromId === rootId) {
                treeItem = tree[oldIndex].toJSON();
                tree.splice(oldIndex, 1);
              } else {
                self.getTreeItemById(parentFromId).setter(function (node) {
                  treeItem = node.childNodes[oldIndex].toJSON();
                  node.childNodes.splice(oldIndex, 1);
                }, true);
              }

              if (process.env.PLUGIN_CTX === "pro") {
                /* onlypro:start */
                requestBody.parent = parentToId;
                /* onlypro:end */
              } // Find destination tree


              if (parentToId === rootId) {
                tree.splice(newIndex, 0, treeItem);
              } else {
                self.getTreeItemById(parentToId).setter(function (node) {
                  node.childNodes.splice(newIndex, 0, treeItem);
                }, true);
              }

              if (request) {
                _context.next = 8;
                break;
              }

              return _context.abrupt("return", true);

            case 8:
              _context.prev = 8;
              _context.next = 11;
              return utilsRequest({
                location: {
                  path: "/hierarchy/" + id,
                  method: "PUT"
                },
                request: requestBody
              });

            case 11:
              return _context.abrupt("return", true);

            case 14:
              _context.prev = 14;
              _context.t0 = _context["catch"](8);
              _context.next = 18;
              return store.handleSort({
                id: id,
                oldIndex: newIndex,
                newIndex: oldIndex,
                parentFromId: parentToId,
                parentToId: parentFromId,
                request: false
              });

            case 18:
              throw _context.t0;

            case 19:
            case "end":
              return _context.stop();
          }
        }
      }, _callee, null, [[8, 14]]);
    })),

    /**
     * Fetch the folder tree.
     *
     * @returns {object[]} Tree
     * @memberof module:store~Store
     * @instance
     * @async
     */
    fetchTree: flow( /*#__PURE__*/_regeneratorRuntime.mark(function _callee2(setSelectedId) {
      var _yield$utilFetchTree, tree, cntRoot, cntAll, slugs, result, node, all;

      return _regeneratorRuntime.wrap(function _callee2$(_context2) {
        while (1) {
          switch (_context2.prev = _context2.next) {
            case 0:
              _context2.next = 2;
              return utilFetchTree();

            case 2:
              _yield$utilFetchTree = _context2.sent;
              tree = _yield$utilFetchTree.tree;
              cntRoot = _yield$utilFetchTree.cntRoot;
              cntAll = _yield$utilFetchTree.cntAll;
              slugs = _yield$utilFetchTree.slugs;
              result = {
                tree: tree,
                cntRoot: cntRoot,
                cntAll: cntAll,
                slugs: slugs
              };
              self.setTree(tree, false, slugs);

              if (typeof setSelectedId !== "undefined") {
                node = self.getTreeItemById(setSelectedId, false);
                node && node.setter(function (node) {
                  return node.selected = true;
                });
              }

              all = self.getTreeItemById("all", false);
              all && all.setter(function (node) {
                return node.count = cntAll;
              });
              self.getTreeItemById(self.rootId, false).setter(function (node) {
                return node.count = cntRoot;
              });
              return _context2.abrupt("return", result);

            case 14:
            case "end":
              return _context2.stop();
          }
        }
      }, _callee2);
    })),

    /**
     * Update the folder count. If you pass no argument the folder count is
     * requested from server.
     *
     * @param {object} counts Key value map of folder and count
     * @returns {object<string|int,int>} Count map
     * @memberof module:store~Store
     * @instance
     * @async
     */
    fetchCounts: flow( /*#__PURE__*/_regeneratorRuntime.mark(function _callee3(counts) {
      return _regeneratorRuntime.wrap(function _callee3$(_context3) {
        while (1) {
          switch (_context3.prev = _context3.next) {
            case 0:
              if (!counts) {
                _context3.next = 3;
                break;
              }

              Object.keys(counts).forEach(function (k) {
                var ref = self.getTreeItemById(k, false);
                ref && (ref.count = counts[k]);
              });
              return _context3.abrupt("return", counts);

            case 3:
              _context3.t0 = self;
              _context3.next = 6;
              return utilsRequest({
                location: {
                  path: "/folders/content/counts"
                }
              });

            case 6:
              _context3.t1 = _context3.sent;
              _context3.next = 9;
              return _context3.t0.fetchCounts.call(_context3.t0, _context3.t1);

            case 9:
              return _context3.abrupt("return", _context3.sent);

            case 10:
            case "end":
              return _context3.stop();
          }
        }
      }, _callee3);
    })),

    /**
     * Create a new tree node.
     *
     * @param {string} name The name of the new folder
     * @param {object} obj The object representing the folder
     * @param {string|int} obj.parent
     * @param {int} obj.typeInt
     * @param {function} [beforeAttach] Callback executed before attaching the new object to the tree
     * @returns {object} The tree node (no mobx model)
     * @memberof module:store~Store
     * @instance
     * @async
     */
    persist: flow( /*#__PURE__*/_regeneratorRuntime.mark(function _callee4(name, _ref3, beforeAttach) {
      var parent, typeInt, newObj;
      return _regeneratorRuntime.wrap(function _callee4$(_context4) {
        while (1) {
          switch (_context4.prev = _context4.next) {
            case 0:
              parent = _ref3.parent, typeInt = _ref3.typeInt;
              _context4.t0 = applyNodeDefaults;
              _context4.next = 4;
              return utilsRequest({
                location: {
                  path: "/folders",
                  method: "POST"
                },
                request: {
                  name: name,
                  parent: parent,
                  type: typeInt
                }
              });

            case 4:
              _context4.t1 = _context4.sent;
              _context4.t2 = [_context4.t1];
              newObj = (0, _context4.t0)(_context4.t2)[0];
              // Add to tree
              beforeAttach && beforeAttach(newObj);

              if (parent === self.rootId) {
                self.tree.push(newObj);
              } else if (process.env.PLUGIN_CTX === "pro") {
                /* onlypro:start */
                self.getTreeItemById(parent).setter(function (node) {
                  node.childNodes.push(newObj);
                }, true);
                /* onlypro:end */
              }

              return _context4.abrupt("return", newObj);

            case 10:
            case "end":
              return _context4.stop();
          }
        }
      }, _callee4);
    }))
  };
});
/**
 * The main Mobx State Tree store for the RML application in the media library view.
 *
 * @class Store
 * @property {mixed[]} [foldersNeedsRefresh] Node ids which needs to be refreshed when they gets queried
 * @property {module:store/Upload~Upload[]} [uploading] The upload queue
 * @property {int} [uploadTotalLoaded=0] The upload total loaded
 * @property {int} [uploadTotalSize=0] The upload total size
 * @property {object} [sortables] Available sortables for the content order menu
 * @property {object} [treeSortables] Available sortables for the tree order menu
 * @property {int} [uploadTotalBytesPerSec=0] The uploader bytes per second
 * @property {module:store/TreeNode~TreeNode} [selected] The selected tree node
 * @property {module:store/Upload~Upload} [currentUpload] The current upload file
 * @property {string} [uploadTotalRemainTime] The current upload remaining time in human readable form
 * @property {string} [readableUploadTotalLoaded] The uploader total loaded in human readable form
 * @property {string} [readableUploadTotalSize] The uploader total size in human readable form
 * @property {string} [readableUploadTotalBytesPerSec] The uploader bytes per second in human readable form
 */

var Store = BasicStore.named("RMLStore").props({
  foldersNeedsRefresh: types.optional(types.array(types.union(types.string, types.number)), []),
  uploading: types.optional(types.array(Upload), []),
  uploadTotalLoaded: types.optional(types.number, 0),
  uploadTotalSize: types.optional(types.number, 0),
  sortables: types.optional(types.frozen()),
  treeSortables: types.optional(types.frozen()),
  uploadTotalBytesPerSec: types.optional(types.number, 0)
}).views(function (self) {
  return {
    get currentUpload() {
      return self.uploading.length ? self.uploading[0] : undefined;
    },

    get uploadTotalRemainTime() {
      if (self.uploadTotalBytesPerSec > 0) {
        var remainTime = Math.floor((self.uploadTotalSize - self.uploadTotalLoaded) / self.uploadTotalBytesPerSec);
        return secondsFormat(remainTime);
      } else {
        return "00:00:00";
      }
    },

    get readableUploadTotalLoaded() {
      return humanFileSize(self.uploadTotalLoaded);
    },

    get readableUploadTotalSize() {
      return humanFileSize(self.uploadTotalSize);
    },

    get readableUploadTotalBytesPerSec() {
      return humanFileSize(self.uploadTotalBytesPerSec);
    }

  };
}).actions(function (self) {
  return {
    /**
     * Set upload total stats.
     *
     * @memberof module:store~Store
     * @instance
     */
    setUploadTotal: function setUploadTotal(_ref4) {
      var loaded = _ref4.loaded,
          size = _ref4.size,
          bytesPerSec = _ref4.bytesPerSec;
      self.uploadTotalLoaded = loaded;
      self.uploadTotalSize = size;
      self.uploadTotalBytesPerSec = bytesPerSec;
    },

    /**
     * Add an uploading file.
     *
     * @param {object} object The object to push
     * @returns {object} The upload instance
     * @memberof module:store~Store
     * @instance
     */
    addUploading: function addUploading(upload) {
      // The tree item needs to be available in the current tree, the upload holds the node so we can safely add it to the tree
      // Imagine: Upload a file directly with "Add media" without the tree ever loaded for the attachment browser
      if (!self.getTreeItemById(upload.node.id, false)) {
        self.tree.push(clone(upload.node));
        self.addFoldersNeedsRefresh(upload.node.id);
      }

      self.uploading.push(upload);
      return self.uploading[self.uploading.length - 1];
    },

    /**
     * Register a folder that it needs refresh.
     *
     * @memberof module:store~Store
     * @instance
     */
    addFoldersNeedsRefresh: function addFoldersNeedsRefresh(id) {
      self.foldersNeedsRefresh.indexOf(id) === -1 && self.foldersNeedsRefresh.push(id);
    },

    /**
     * Register a folder that it needs refresh.
     *
     * @memberof module:store~Store
     * @instance
     */
    removeFoldersNeedsRefresh: function removeFoldersNeedsRefresh(id) {
      var idx = self.foldersNeedsRefresh.indexOf(id);
      idx > -1 && self.foldersNeedsRefresh.splice(idx, 1);
    },

    /**
     * Remove an uploading file from queue.
     *
     * @param {string} cid The cid
     * @returns {object} A copy of the original object
     * @memberof module:store~Store
     * @instance
     */
    removeUploading: function removeUploading(cid) {
      for (var i = 0; i < self.uploading.length; i++) {
        if (self.uploading[i].cid === cid) {
          var copy = self.uploading[i].toJSON();
          self.uploading.splice(i, 1);
          return copy;
        }
      }
    }
  };
});
export var createUnorganizedNode = function createUnorganizedNode() {
  return {
    id: +rmlOpts.others.rootId,
    title: rmlOpts.others.lang.unorganized,
    icon: "home",
    count: 0,
    contentCustomOrder: 2,
    properties: {
      type: 4
    }
  };
};
export var createAllNode = function createAllNode() {
  return {
    id: "all",
    title: rmlOpts.others.lang.allPosts,
    icon: "copy",
    count: rmlOpts.others.allPostCnt
  };
};
/**
 * Main store instance.
 */

var store = Store.create({
  staticTree: [{
    id: -2,
    title: "none",
    $visible: false,
    properties: {
      type: -2
    }
  }, createAllNode(), createUnorganizedNode()],
  sortables: rmlOpts.others.sortables.content,
  treeSortables: rmlOpts.others.sortables.tree
});
/**
 * A single instance of store.
 */

export default store;
/**
 * An AppTree implementation with store provided. This means you have no longer
 * implement the Provider of mobx here.
 *
 * @returns {React.Element}
 */

export var StoredAppTree = function StoredAppTree(_ref5) {
  var children = _ref5.children,
      useStore = _ref5.useStore,
      rest = _objectWithoutProperties(_ref5, ["children", "useStore"]);

  return /*#__PURE__*/React.createElement(Provider, {
    store: useStore ? useStore : store
  }, /*#__PURE__*/React.createElement(AppTree, rest, children));
};
/**
 * Import general store to ReactJS component.
 */

export function injectAndObserve(fn) {
  var store = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : "store";
  return inject(store)(observer(fn));
}
export { TreeNode, Upload, BasicStore };